import {Alert, Card, Space, Spin} from "antd";
import {Formik} from "formik";
import moment from "moment";
import React, {FC, memo, useEffect, useRef, useState} from "react";
import {KnownError} from "../../../errors/handleErrors";
import {
    CreateSepaXmlByTimeRangeInput,
    GetExportedSepaXmlsDocument,
    NewSepaXmlInput,
    useCreateSepaXmlByTimeRangeMutation,
} from "../../../generated/graphql";
import {colorBlueLight} from "../../../styles/colors";
import {isDefined} from "../../../helpers/typeScriptHelpers";
import {panelWidth} from "../../../styles/layout";
import AlertCard from "../../../views/AlertCard/AlertCard";
import ExportedSepaXmlList from "../../exportedSepaXml/ExportedSepaXmlList";
import {buildTimeRangeInput} from "./buildTimeRangeInput";
import {convertXmlData} from "./convertXmlData";
import CreateXmlModalPreview, {CreateXmlModalPreviewProps} from "./CreateXmlModalPreview";
import CreateXmlModalTable from "./CreateXmlModalTable";
import InputForm from "./InputForm";
import Description from "./Description";
import DraggableModal from "../../../views/DraggableModal/DraggableModal";
import {createPreviewCoordinator, PreviewCoordinator} from "./previewCoordinator";
import {downLoadXmlFile} from "./downloadXmlFile";
import {hasSepaPreviewPaymentCount} from "./sepaExportGuard";

type CreateXmlModalProps = {
    open: boolean;
    setOpen: (open: boolean) => void;
    now: Date;
};

/**
 * `limitReached` liefert der Server additiv im JSON-Objekt der Antwort (Folge-PR zu Issue #671):
 * true, sobald das `maxAmount`-Limit gegriffen hat und noch weitere offene Raten existieren.
 * Solange das Feld fehlt, bleibt es schlicht `undefined`.
 */
/**
 * Rate, die der Server wegen ungültiger IBAN/BIC (#899) oder fehlender Mandatsdaten
 * (B-82808, `incomplete`) aus der Datei ausgeschlossen hat (additiv).
 */
export type InvalidBankDataEntry = {scheduledPaymentId: string; customerNumber: string; debtorName: string; issue: "iban" | "bic" | "incomplete"};

const invalidBankDataIssueText = (issue: InvalidBankDataEntry["issue"]): string => {
    if (issue === "iban") {
        return "IBAN-Prüfziffer falsch";
    }
    if (issue === "bic") {
        return "BIC-Format ungültig";
    }

    return "Bankdaten unvollständig (Kontoinhaber, IBAN oder BIC fehlt) – Rate wird nicht exportiert";
};

export type XmlResponse = {
    object: NewSepaXmlInput;
    xml: string;
    savedFile?: boolean;
    limitReached?: boolean;
    selectedCount?: number;
    safetyCapApplied?: boolean;
    /** Höchstens 50 Einträge; die Gesamtzahl steht in invalidBankDataCount. */
    invalidBankData?: Array<InvalidBankDataEntry>;
    invalidBankDataCount?: number;
};

/** Vorbelegung: keine Anzahlgrenze (die harte 2000er-Grenze aus #671 wurde 2026-09-14 entfernt). */
const DEFAULT_MAX_AMOUNT: number | undefined = undefined;

type ErrorEntry = {errorTitle: string; errorMsg: string};

const CreateXmlModal: FC<CreateXmlModalProps> = ({open, setOpen, now}) => {
    // https://www.mobilefish.com/services/sepa_xml_validation/sepa_xml_validation.php

    const cardWidth = panelWidth - 45;

    const [createSepaXmlByTimeRangeInput, setCreateSepaXmlByTimeRangeInput] = useState<CreateSepaXmlByTimeRangeInput>({
        startDate: undefined,
        endDate: undefined,
        collectionDate: undefined,
        saveFile: false,
        maxAmount: DEFAULT_MAX_AMOUNT,
        maxMoneyAmount: undefined,
    });

    // Preview- und Write-Fehler werden getrennt gehalten: ein (ggf. verspäteter) Preview-Fehler
    // darf einen erfolgreichen Write nicht überschreiben — und umgekehrt.
    const [previewErrors, setPreviewErrors] = useState<Array<ErrorEntry> | undefined>();
    const [writeErrors, setWriteErrors] = useState<Array<ErrorEntry> | undefined>();
    const errors = writeErrors ?? previewErrors;

    const [xmlData, setXmlData] = useState<XmlResponse>();

    // Zwei separate Mutation-Hooks ⇒ getrennte Loading-Zustände für Vorschau und Export.
    const [getXmlDataPreview, {loading: previewLoading}] = useCreateSepaXmlByTimeRangeMutation();
    const [getXmlDataWrite, {loading: writeLoading}] = useCreateSepaXmlByTimeRangeMutation();

    const ErrorSegment = ({errors}: {errors?: Array<ErrorEntry>}) => {
        if (!errors || errors.length === 0) {
            return null;
        }

        const ErrorList: Array<JSX.Element> = [];

        errors.forEach((error) => {
            ErrorList.push(<AlertCard key={error.errorTitle} description={error.errorMsg} title={error.errorTitle} showIcon={false} type="DANGER" />);
        });

        return <>{ErrorList}</>;
    };

    const knownErrors: Array<KnownError> = [
        {
            key: "No scheduled payments found",
            title: "Keine Zahlungen",
            msg: "Keine Sepa-Zahlungen für den angegebenen Zeitraum gefunden",
        },
        {
            key: "No company creditorId",
            title: "Keine Gläubiger ID",
            msg: "Um die XML-Datei zu erstellen, muss eine Gläubiger-ID unter Einstellungen > Bankverbindung hinterlegt sein.",
        },
        {
            key: "No attendee or Booker found",
            title: "Bucher oder Teilnehmer nicht gefunden",
            msg: "Bei einem oder mehreren Buchungen konnte kein Teilnehmer oder Bucher gefunden werden. Bitte überprüfe die Buchungen.",
        },
        {
            key: "No course found",
            title: "Kurs nicht gefunden",
            msg: "Bei einer oder mehreren Buchungen konnte kein Kurs gefunden werden. Bitte überprüfe die Buchungen.",
        },
        {
            key: "No company name found",
            title: "Fehlender Firmenname",
            msg: "Um die XML-Datei zu erstellen, muss ein Firmenname in den Stammdaten hinterlegt sein.",
        },
        {
            key: "No company IBAN found",
            title: "Fehlende Iban",
            msg: "Um die XML-Datei zu erstellen, muss eine Iban in den Einstellungen hinterlegt sein.",
        },
        {
            key: "creditorIBAN has invalid IBAN",
            title: "Fehlerhafte Iban",
            msg: "Die angegebene Iban unter Einstellungen > Bankverbindung ist fehlerhaft. Bitte überprüfe die Iban.",
        },
        {
            key: "No company BIC found",
            title: "Fehlende BIC",
            msg: "Um die XML-Datei zu erstellen, muss eine BIC in den Einstellungen hinterlegt sein.",
        },
        {
            key: "country mismatch in BIC/IBAN",
            title: "Fehlerhafte BIC",
            msg: "Die angegebene BIC unter Einstellungen > Bankverbindung ist fehlerhaft. Bitte überprüfe die BIC.",
        },
        {
            key: "creditorId is invalid",
            title: "Fehlerhafte Gläubiger ID",
            msg: "Die angegebene Gläubiger-ID unter Einstellungen > Bankverbindung ist fehlerhaft. Bitte überprüfe die Gläubiger-ID.",
        },
        {
            key: "No sepaAccountholder",
            title: "Fehlende Kontoinhaberdaten",
            msg: "Um die XML-Datei zu erstellen, muss ein Kontoinhaber in den Einstellungen hinterlegt sein. Diese fehlen bei mindestens einem Bucher.",
        },
    ];

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const toErrorEntries = (error: any): Array<ErrorEntry> | undefined => {
        if (!error?.graphQLErrors || error.graphQLErrors.length === 0) {
            return error?.networkError
                ? [{errorTitle: "Fehler", errorMsg: "Die Anfrage konnte nicht ausgeführt werden. Bitte versuche es erneut."}]
                : [{errorTitle: "Fehler", errorMsg: error?.message ? error.message : "Unbekannter Fehler"}];
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return error.graphQLErrors.map((gqlError: any) => {
            const gqlErrorMsg: string = gqlError.message;
            const knownError = knownErrors.find((candidate) => gqlErrorMsg.includes(candidate.key));

            return knownError ? {errorTitle: knownError.title, errorMsg: knownError.msg} : {errorTitle: "Fehler", errorMsg: gqlErrorMsg};
        });
    };

    // PREVIEW-KOORDINATOR ---------------------------------------------------------------------------
    // Einzige Stelle, die Vorschau-Requests (saveFile:false) auslöst — dedupliziert + single-flight.

    type PreviewHandlers = {
        run: (input: CreateSepaXmlByTimeRangeInput) => Promise<XmlResponse | undefined>;
        apply: (result?: XmlResponse) => void;
        onError: (error: unknown) => void;
    };

    // Die Handler werden pro Render aktualisiert, der Koordinator selbst bleibt stabil.
    const previewHandlersRef = useRef<PreviewHandlers>({
        run: () => Promise.resolve(undefined),
        apply: () => undefined,
        onError: () => undefined,
    });

    previewHandlersRef.current = {
        run: async (input) => {
            const response = await getXmlDataPreview({
                variables: {createSepaXmlByTimeRangeInput: {...input, saveFile: false}},
            });

            return response.data?.createSepaXmlByTimeRange;
        },
        apply: (result) => {
            setPreviewErrors(undefined);
            setXmlData(result);
        },
        onError: (error) => {
            setPreviewErrors(toErrorEntries(error));
        },
    };

    const coordinatorRef = useRef<PreviewCoordinator>();

    if (!coordinatorRef.current) {
        coordinatorRef.current = createPreviewCoordinator<XmlResponse | undefined>({
            run: (input) => previewHandlersRef.current.run(input),
            apply: (result) => previewHandlersRef.current.apply(result),
            onError: (error) => previewHandlersRef.current.onError(error),
        });
    }
    const coordinator = coordinatorRef.current;

    const requestPreview = (values: CreateSepaXmlByTimeRangeInput) => {
        const timeRangeInput = buildTimeRangeInput({
            startDate: values.startDate,
            endDate: values.endDate,
            collectionDate: values.collectionDate,
            saveFile: false,
            maxAmount: isDefined(values.maxAmount) ? values.maxAmount : undefined,
            maxMoneyAmount: isDefined(values.maxMoneyAmount) ? values.maxMoneyAmount : undefined,
        });

        setCreateSepaXmlByTimeRangeInput(timeRangeInput);

        coordinator.request(timeRangeInput);
    };

    // Keine automatische Vorschau beim Öffnen: Ohne Zeitraum und ohne Anzahlgrenze würde sie alle
    // offenen Raten des Mandanten hydratisieren (Review zu PR #880). Die erste Vorschau läuft erst
    // nach einer Eingabe (DatePicker schliessen, Zahlenfeld verlassen); bis dahin zeigt XmlPreview
    // „Keine Zahlungen" plus Exporthistorie. Beim Schliessen wird der Koordinator entwertet.
    useEffect(() => {
        if (!open) {
            coordinator.reset();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // WRITE-PFAD ------------------------------------------------------------------------------------

    const saveXmlFile = async (input: CreateSepaXmlByTimeRangeInput): Promise<void> => {
        setWriteErrors(undefined);

        try {
            const response = await getXmlDataWrite({
                variables: {createSepaXmlByTimeRangeInput: {...input, saveFile: true}},
                // Offene und bezahlte Posten per Name (Operationen klein geschrieben): lädt nur die offene
                // Liste mit ihren Variablen. Die früheren {query: …ScheduledPaymentsDocument} ohne Variablen
                // trafen sie nie, die Liste zeigte exportierte Raten bis zum Neuladen weiter als offen.
                refetchQueries: ["getUnpaidScheduledPayments", "getPaidScheduledPayments", {query: GetExportedSepaXmlsDocument}],
            });

            const result: XmlResponse | undefined = response.data?.createSepaXmlByTimeRange;

            // Die Write-Antwort wird heruntergeladen, aber NICHT als neue Vorschau übernommen.
            if (result?.savedFile === true) {
                downLoadXmlFile({xmlData: result});
            }

            // Laufende/vorgemerkte Previews entwerten (eine verspätete Antwort darf den Zustand nach
            // dem Export nicht überschreiben) und danach GENAU EINE Vorschau nachladen: sie zeigt die
            // verbleibenden offenen Raten für die nächste Datei (Folgedatei-Workflow bei gesetzter Anzahlgrenze).
            // Sequentiell, nicht parallel zum Write.
            coordinator.reset();
            setPreviewErrors(undefined);
            coordinator.request({...input, saveFile: false});
        } catch (error) {
            setWriteErrors(toErrorEntries(error));
        }
    };

    const XmlPreview = ({xmlData}: {xmlData?: XmlResponse}) => {
        const [activeTabKey, setActiveTab] = useState("tab1");

        if (!xmlData || !xmlData.object) {
            // Bewusst KEIN setPreviewErrors während des Renderns: Seit der Dialog ohne Auto-Vorschau
            // öffnet, stand der Fehler bereits beim Öffnen im State. Die erste Antwort setzte dann
            // in `apply` erst previewErrors zurück und danach xmlData — in React 17 sind diese beiden
            // Updates aus einem Promise-Callback nicht gebündelt, der Zwischen-Render (noch ohne
            // xmlData) setzte den Fehler sofort wieder, und die Vorschau blieb dauerhaft leer
            // (Prod 2026-09-14: 2903 Raten geliefert, „Keine Zahlungen" angezeigt).
            const noPaymentsYet: Array<ErrorEntry> = [{errorMsg: knownErrors[0].msg, errorTitle: knownErrors[0].title}];

            return (
                <Space direction="vertical" style={{width: "100%"}}>
                    <ErrorSegment errors={noPaymentsYet} />
                    <ExportedSepaXmlList />
                </Space>
            );
        }
        // CONVERT THE FETCHED XML DATA TO THE RIGHT FORMAT ----------------------------------------------

        const convertedXmlData: CreateXmlModalPreviewProps = convertXmlData({
            xmlData,
        });

        const {documentInfo, creditorInfo, transactionInfo} = convertedXmlData;

        // TABLE VIEW ----------------------------------------------------------------------------------

        const tab1 = <CreateXmlModalTable documentInfo={documentInfo} creditorInfo={creditorInfo} transactionInfo={transactionInfo} />;

        // CARD VIEW ----------------------------------------------------------------------------------

        const tab2 = <CreateXmlModalPreview documentInfo={documentInfo} creditorInfo={creditorInfo} transactionInfo={transactionInfo} panelWidth={panelWidth} cardWidth={cardWidth} />;

        // EXPORT HISTORY ----------------------------------------------------------------------------------<

        const tab3 = <ExportedSepaXmlList />;

        // CARD ----------------------------------------------------------------------------------

        const tabList = [
            {
                key: "tab1",
                tab: "Tabellenansicht",
            },
            {
                key: "tab2",
                tab: "Vorschau Begleitzettel",
            },
            {
                key: "tab3",
                tab: "Exporthistorie",
            },
        ];

        const contentList: Record<string, React.ReactNode> = {
            tab1,
            tab2,
            tab3,
        };

        const onTabChange = (key: string) => {
            setActiveTab(key);
        };

        return (
            <Card
                style={{
                    maxWidth: cardWidth,
                    width: "100%",
                    boxShadow: "0 0 30px 0 rgba(0, 0, 0, 0.3)",
                    display: "flex",
                    flexDirection: "column",
                    borderColor: colorBlueLight,
                    borderRadius: "10px",
                }}
                tabList={tabList}
                activeTabKey={activeTabKey}
                onTabChange={(key) => {
                    onTabChange(key);
                }}
            >
                {contentList[activeTabKey]}
            </Card>
        );
    };

    //  ----------------------------------------------------------------------------------------------------------------

    const initialValues: CreateSepaXmlByTimeRangeInput = {
        startDate: undefined,
        endDate: undefined,
        collectionDate: undefined,
        maxAmount: DEFAULT_MAX_AMOUNT,
        maxMoneyAmount: undefined,
    };

    const paymentCount = xmlData?.object?.positions?.[0]?.payments?.length;
    // Auch ohne xmlData sperren: sonst bleibt der Export nach #882 klickbar, obwohl
    // die Fläche „Keine Zahlungen" zeigt, und createByTimeRange würde ohne Zeitraum
    // alle offenen Lastschriften des Mandanten verbuchen.
    const noPayments = !hasSepaPreviewPaymentCount(paymentCount);

    // Warnt, wenn der gewählte End-TAG in der Zukunft liegt. endDate ist seit dem buildTimeRangeInput-Helper
    // ein vollständiger ISO-String (lokales Tagesende, …T21:59:59.999Z); das frühere `endDate + "T00:00:00.000Z"`
    // hängte einen zweiten Zeit-Suffix an → ungültiges moment → Banner wurde nie angezeigt. Vergleich gegen den
    // lokalen Tagesanfang des Enddatums erhält die ursprüngliche „End-Tag in der Zukunft"-Semantik.
    const currentDateIsBeforeEndDate = createSepaXmlByTimeRangeInput.endDate ? moment(now).isBefore(moment(createSepaXmlByTimeRangeInput.endDate).startOf("day")) : false;

    return (
        <DraggableModal
            data-testid="create-xml-modal"
            draggable={true}
            boundsPadding={500}
            title="Sepa-Xml-Datei erstellen"
            open={open}
            onCancel={() => {
                setOpen(false);
                setXmlData(undefined);
            }}
            width="95%"
            style={{maxWidth: panelWidth, top: 30}}
            destroyOnClose
            footer={null}
            bodyStyle={{
                padding: 20,
                overflow: "auto",
                maxHeight: `calc(100vh - 60px - ${20 * 4}px)`,
            }}
        >
            <Space direction="vertical">
                <Description />
                {/* #719: Ohne Zeitraum liefert die Vorschau limitReached aus der Kandidatenzählung, aber
                    keine Zeilen; „Limit erreicht" neben „Keine Zahlungen" ist dann irreführend. Alte
                    Server-Antworten ohne selectedCount verhalten sich wie bisher. */}
                {xmlData?.limitReached === true && (xmlData.selectedCount === undefined || xmlData.selectedCount > 0) && (
                    <Alert
                        type="info"
                        showIcon
                        message="Limit erreicht"
                        description={
                            // #892: Ohne Zeitraum und ohne Anzahlgrenze deckelt der Server auf 2000 Raten.
                            xmlData.safetyCapApplied === true
                                ? "Ohne Zeitraum und ohne Anzahlgrenze werden höchstens 2000 Raten pro Datei exportiert. Wähle einen Fälligkeitszeitraum oder exportiere die verbleibenden Raten anschliessend in einer Folgedatei."
                                : "Die eingestellte Anzahlgrenze wurde erreicht. Die verbleibenden offenen Raten kannst du anschliessend in einer Folgedatei exportieren."
                        }
                    />
                )}
                {xmlData?.invalidBankData !== undefined && xmlData.invalidBankData.length > 0 && (
                    <Alert
                        type="warning"
                        showIcon
                        data-testid="CreateXmlModalInvalidBankData"
                        message={`${xmlData.invalidBankDataCount ?? xmlData.invalidBankData.length} Rate(n) wegen ungültiger oder unvollständiger Bankdaten NICHT in der Datei`}
                        description={
                            <>
                                <div>{"Diese Raten bleiben offen. Bitte Bankdaten beim Bucher vervollständigen bzw. korrigieren und den Export danach für die verbleibenden Raten wiederholen."}</div>
                                {(xmlData.invalidBankDataCount ?? 0) > xmlData.invalidBankData.length && (
                                    <div>{`Angezeigt werden die ersten ${xmlData.invalidBankData.length} Raten.`}</div>
                                )}
                                <ul style={{marginBottom: 0}}>
                                    {xmlData.invalidBankData.map((entry) => (
                                        <li key={entry.scheduledPaymentId}>
                                            {`Kundennr. ${entry.customerNumber}, ${entry.debtorName || "(kein Kontoinhaber)"}: ${invalidBankDataIssueText(entry.issue)}`}
                                        </li>
                                    ))}
                                </ul>
                            </>
                        }
                    />
                )}
                <Space direction="vertical" style={{width: "100%"}}>
                    <Card
                        title="Zeitraum wählen"
                        style={{
                            display: "flex",
                            borderColor: colorBlueLight,
                            borderRadius: "10px",
                            boxShadow: "0 0 30px 0 rgba(0, 0, 0, 0.3)",
                            marginBottom: 30,
                            flexDirection: "column",
                            width: "100%",
                        }}
                    >
                        <Formik
                            initialValues={initialValues}
                            enableReinitialize
                            onSubmit={(values) => {
                                requestPreview(values);
                            }}
                        >
                            {(formikProps) => {
                                return (
                                    <InputForm
                                        formikProps={formikProps}
                                        currentDateIsBeforeEndDate={currentDateIsBeforeEndDate}
                                        createSepaXmlByTimeRangeInput={createSepaXmlByTimeRangeInput}
                                        onPreviewRequest={requestPreview}
                                        setCreateSepaXmlByTimeRangeInput={setCreateSepaXmlByTimeRangeInput}
                                        setXmlData={setXmlData}
                                        loadXmlData={saveXmlFile}
                                        noPayments={noPayments}
                                        paymentCount={paymentCount}
                                        errors={errors}
                                        loading={writeLoading}
                                    />
                                );
                            }}
                        </Formik>
                    </Card>
                </Space>
                {previewLoading || writeLoading ? (
                    <div style={{display: "flex", justifyContent: "center"}}>
                        <Spin />
                    </div>
                ) : (
                    <>
                        {errors ? (
                            <>
                                <ErrorSegment errors={errors} />
                                {errors[0].errorTitle === "Keine Zahlungen" && <ExportedSepaXmlList />}
                            </>
                        ) : (
                            <XmlPreview xmlData={xmlData} />
                        )}
                    </>
                )}
            </Space>
        </DraggableModal>
    );
};

export default memo(CreateXmlModal);
